
import React, { useCallback, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { useSelector } from "react-redux";
import toast from "react-hot-toast";
import { ResourceLoading } from "../../../components/utils/ResourceLoading";
import { ResourceError } from "../../../components/utils/ResourceError";
import { useUrlTab } from "../../../components/utils/useUrlTab";

import { ProjectInfo } from "../../../components/dashboard/projects/project-info";
import {
  CameraIcon,
  ExclamationCircleIcon,
  FolderIcon,
  PencilIcon,
  PaperClipIcon,
  WrenchScrewdriverIcon,
  CheckCircleIcon,
  TableCellsIcon,
  Cog6ToothIcon,
  PrinterIcon,
  ClockIcon,
  ClipboardDocumentListIcon,
  DocumentTextIcon,
  TruckIcon,
  ChartBarIcon,
  CogIcon,
  GlobeAltIcon,
  ClipboardDocumentCheckIcon,
  ListBulletIcon,
  BookOpenIcon,
  ChatBubbleLeftRightIcon,
  CubeIcon,
} from "@heroicons/react/24/outline";
import DashboardHeader from "../../../components/dashboard/dashboardHeader";
import JournalSection from "../../../components/dashboard/journaling/JournalSection";
import { StatusUpdateSection } from "../../../components/dashboard/statusUpdates/StatusUpdateSection";
import TextDocumentsSection from "../../../components/dashboard/text-documents/TextDocumentsSection";
import { mapErrors } from "../../../components/Utils";
import { mergeRouterAndWindowSearchParams } from "../../../components/dashboard/projects/projectTimeOverviewSearchParams";
import { fetchProject, exportProjectOverviewPdf, retryProjectTripletexSync } from "../../../api/project";
import { ReportsStackList } from "../../../components/dashboard/service/reports/reports-stack-list";
import { ServiceAgreementsStackList } from "../../../components/dashboard/service/agreements/agreements-stack-list";
import { projectArchivePath } from "../../../components/dashboard/projects/folders/archive-utils";
import { DirectImageUploadButton } from "../../../components/dashboard/Media/DirectImageUploadButton";
import { FolderCreateDialog } from "../../../components/dashboard/projects/folders/folder-create-dialog";
import { DeviationStacklist } from "../../../components/dashboard/quality/deviations/deviationStacklist";
import { ChecklistStacklist } from "../../../components/dashboard/quality/checklists/checklistStacklist";
import { EnhancedChecklistSelector } from "../../../components/dashboard/service/reports/enhanced-checklist-selector";
import { createChecklist, fetchChecklists } from "../../../api/quality/checklist";
import { writeDrawingListState } from "../../../components/dashboard/drawings/drawingStructureState";
import { IfcViewerPanel } from "../../../components/dashboard/ifc-viewer/ifc-viewer-panel";
import { FieldsSection } from "../../../components/dashboard/fields/section";
import ServiceReportEditModal from "../../../components/dashboard/service/reports/service-report-edit-modal";
import { LabelDesignerDialog } from "../../../components/labels/LabelDesignerDialog";
import { projectRecord } from "../../../components/labels/labelRecords";
import {
  useUserHasAccess,
  useProjectPermissions,
  useServiceReportsAccess,
  useServiceAgreementsAccess,
  useWorkOrdersAccess,
  useQualityDeviationsAccess,
  useProjectServiceAgreementsAccess,
  useProjectServiceReportsAccess,
  useProjectWorkOrdersAccess,
  useProjectQualityDeviationsAccess,
  useProjectQualityChecklistsAccess,
  useCompanyHasModule,
  useProjectAccountingAccess,
} from "../../../routes/roleOnly";
import { WorkOrderStacklist } from "../../../components/dashboard/workOrders/work-order-stacklist";

// Import new project management components
import { TimeRegistrationTab } from "../../../components/dashboard/projects/tabs/time-registration-tab";
import { ItemListTab } from "../../../components/dashboard/projects/tabs/item-list-tab";
import { InvoicesTab } from "../../../components/dashboard/projects/tabs/invoices-tab";
import { SupplierInvoicesTab } from "../../../components/dashboard/projects/tabs/supplier-invoices-tab";
import { BillingPlanTab } from "../../../components/dashboard/projects/tabs/billing-plan-tab";
import { ProjectSettingsTab } from "../../../components/dashboard/projects/tabs/project-settings-tab";
import { ProjectSummaryTab } from "../../../components/dashboard/projects/tabs/project-summary-tab";
import { WebsiteTab } from "../../../components/dashboard/projects/tabs/website-tab";
import { ProjectActivitiesTab } from "../../../components/dashboard/projects/tabs/project-activities-tab";
import { ProjectHseHandbookTab } from "../../../components/dashboard/projects/tabs/project-hse-handbook-tab";
import { ProjectTasksTab } from "../../../components/dashboard/projects/tabs/project-tasks-tab";
import { ProjectMobileActionsNav } from "../../../components/dashboard/projects/project-mobile-actions-nav";
import { ProjectViewHero } from "../../../components/dashboard/projects/ProjectViewHero";
import { ProjectCommandPalette } from "../../../components/dashboard/projects/ProjectCommandPalette";
import { ProjectFab } from "../../../components/dashboard/projects/ProjectFab";
import "../../../components/dashboard/projects/project-view-redesign.css";

/** Tab name used by the `?tab=` deep link from the drawings module. */
export const IFC_VIEWER_TAB_NAME = "IFC Viewer";

/** Stable key for prosjekt-timeoversikt. Legacy name still accepted in `?tab=`. */
export const TIME_OVERVIEW_TAB_KEY = "timeoversikt";
export const TIME_OVERVIEW_TAB_ALIASES = [
  "timeoversikt",
  "Timeoversikt",
  "Timeregistrering",
];

export const Project = (props) => {
  const { t } = useTranslation();
  const { id } = useParams();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const currentUserId = useSelector((state) => state.session.currentUser?.id);

  const [itemState, setItemState] = useState({
    isLoading: true,
    error: false,
    errors: [],
    project: {},
  });

  const { project } = itemState;

  const projectPermissions = useProjectPermissions(project);
  const canProjectAccounting = useProjectAccountingAccess();
  const canEditAccountingUi =
    canProjectAccounting && (projectPermissions.canEdit || projectPermissions.isAdmin);

  const serviceAgreementAccess = useProjectServiceAgreementsAccess(project);
  const serviceReportAccess = useProjectServiceReportsAccess(project);
  const workOrderAccess = useProjectWorkOrdersAccess(project);
  const qualityDeviationAccess = useProjectQualityDeviationsAccess(project);
  const qualityChecklistAccess = useProjectQualityChecklistsAccess(project);
  const websiteModuleActive = useCompanyHasModule("website");
  const drawingsModuleActive = useCompanyHasModule("drawings");
  const ifcViewerModuleActive = useCompanyHasModule("ifc_viewer");
  const ifcViewerAccess = useUserHasAccess({
    roles: ["ifc_viewer", "ifc_viewer_edit", "admin"],
  });
  const ifcViewerCanEdit = useUserHasAccess({
    roles: ["ifc_viewer_edit", "admin"],
  });
  const ifcViewerIsAdmin = useUserHasAccess({
    roles: ["admin"],
  });
  const [reportDialogOpen, setReportDialogOpen] = useState(false);
  const [folderCreateDialogOpen, setFolderCreateDialogOpen] = useState(false);
  const [checklistTypeSelectorOpen, setChecklistTypeSelectorOpen] =
    useState(false);
  const [checklistDefaultTitle, setChecklistDefaultTitle] = useState("");
  const [isCreatingChecklist, setIsCreatingChecklist] = useState(false);
  const photoPickerRef = useRef(null);
  const tabsRef = useRef([]);
  const selectTabRef = useRef(() => {});
  const [paletteOpen, setPaletteOpen] = useState(false);
  const [exportingPdf, setExportingPdf] = useState(false);

  const ex = projectPermissions.isExecutorParticipant;

  const rightSideActions = [
    {
      label: t("project.actions.new_deviation"),
      mobileLabel: t("project.actions.new_deviation_short"),
      description: t("project.actions.new_deviation_description"),
      path: "/dashboard/hse-ks/deviations/new",
      action: () =>
        navigate("/dashboard/hse-ks/deviations/new", {
          state: {
            project: project,
            customer: project?.customer,
          },
        }),
      icon: ExclamationCircleIcon,
      iconForeground: "text-teal-700",
      iconBackground: "bg-teal-50",
      active: !ex && qualityDeviationAccess.canCreate,
    },
    {
      label: t("project.checklist.create"),
      mobileLabel: t("project.actions.new_checklist_short"),
      description: t("project.checklist.create_description"),
      path: "",
      action: () => setChecklistTypeSelectorOpen(true),
      icon: ClipboardDocumentCheckIcon,
      iconForeground: "text-teal-700",
      iconBackground: "bg-teal-50",
      active: !ex && qualityChecklistAccess.canCreate,
    },
    {
      label: t("project.actions.service_report"),
      mobileLabel: t("project.actions.service_report_short"),
      description: t("project.actions.service_report_description"),
      path: "",
      action: () => setReportDialogOpen(true),
      icon: Cog6ToothIcon,
      iconForeground: "text-teal-700",
      iconBackground: "bg-teal-50",
      active: !ex && serviceReportAccess.canCreate,
    },
    {
      label: t("project.actions.work_order"),
      mobileLabel: t("project.actions.work_order_short"),
      description: t("project.actions.work_order_description"),
      path: "/dashboard/work_orders/new",
      action: () =>
        navigate("/dashboard/work_orders/new", {
          state: {
            project: project,
            customer: project?.customer,
            contact: project?.contact,
            delivery_address: project?.delivery_address,
          },
        }),
      icon: WrenchScrewdriverIcon,
      iconForeground: "text-teal-700",
      iconBackground: "bg-teal-50",
      active: !ex && workOrderAccess.canCreate,
    },
    {
      label: t("project.actions.new_folder"),
      mobileLabel: t("project.actions.new_folder_short"),
      description: t("project.actions.new_folder_description"),
      path: "",
      action: () => setFolderCreateDialogOpen(true),
      icon: FolderIcon,
      iconForeground: "text-teal-700",
      iconBackground: "bg-teal-50",
      active: projectPermissions.canManageFolders,
    },
    {
      label: t("documentUpload.direct.buttonShort"),
      mobileLabel: t("project.view.mobileNav.takePhoto"),
      description: t("documentUpload.direct.tooltip"),
      path: "",
      directUpload: true,
      action: () => photoPickerRef.current?.(),
      icon: CameraIcon,
      iconForeground: "text-emerald-700",
      iconBackground: "bg-emerald-50",
      active: projectPermissions.canEdit,
    },
    {
      label: t("project.actions.register_hours"),
      mobileLabel: t("project.actions.register_hours_short"),
      description: t("project.actions.register_hours_description"),
      path: "",
      action: () => {
        const names = tabsRef.current || [];
        const index = names.findIndex(
          (tab) =>
            tab.tabKey === TIME_OVERVIEW_TAB_KEY ||
            TIME_OVERVIEW_TAB_ALIASES.includes(tab.name)
        );
        if (index >= 0) selectTabRef.current(index);
      },
      icon: ClockIcon,
      iconForeground: "text-teal-700",
      iconBackground: "bg-teal-50",
      active: projectPermissions.canTimeRegister,
    },
  ];

  const getProject = async () => {
    if (!id) return;
    
    return fetchProject(id)
      .then((res) => {
        const status = res?.status;
        const payload = res?.data;
        if (status >= 400 || !payload?.id) {
          setItemState((prevState) => ({
            ...prevState,
            isLoading: false,
            project: {},
            error: true,
            errors:
              typeof payload?.errors === "string"
                ? [payload.errors]
                : payload?.errors != null
                ? mapErrors(payload.errors)
                : [t("common.unexpected_error")],
          }));
          if (status === 403 || status === 404) {
            navigate("/dashboard/projects", { replace: true });
          }
          return;
        }
        setItemState((prevState) => ({
          ...prevState,
          isLoading: false,
          project: payload,
        }));
      })
      .catch((err) => {
        let errors = null;
        if (err?.data?.errors) {
          errors = mapErrors(err.data.errors);
        } else {
          errors = [t("common.unexpected_error")];
        }
        setItemState((prevState) => ({
          ...prevState,
          isLoading: false,
          project: {},
          error: true,
          errors: errors,
        }));
        if (err?.status === 403 || err?.status === 404) {
          navigate("/dashboard/projects", { replace: true });
        }
      });
  };

  useEffect(() => {
    if (!id) return;

    // Reset state when id changes
    setItemState((prevState) => ({
      ...prevState,
      isLoading: true,
      error: false,
      errors: [],
      project: {},
    }));

    // Fetch project
    getProject();
  }, [id]);

  const handleOnRefresh = (data) => {
    if (data?.id) {
      setItemState((prevState) => ({
        ...prevState,
        project: data,
      }));
    } else {
      getProject();
    }
  };

  const handleRetryProjectSync = useCallback(async () => {
    if (!id) return null;
    const res = await retryProjectTripletexSync(id);
    if (res?.tripletex_sync) {
      setItemState((prevState) => ({
        ...prevState,
        project: {
          ...prevState.project,
          tripletex_sync: res.tripletex_sync,
        },
      }));
    }
    return res;
  }, [id]);

  const handleFolderCreated = (newFolder) => {
    // Refresh the project data or folders list
    console.log("New folder created:", newFolder);
    // You might want to refresh the folders list here
  };

  const { isLoading, error, errors } = itemState;

  const item = project;

  useEffect(() => {
    if (!checklistTypeSelectorOpen || !item?.id) {
      return undefined;
    }

    let cancelled = false;

    const loadDefaultTitle = async () => {
      try {
        const res = await fetchChecklists({
          filters: [{ name: "project", value: item.id }],
          per_page: 1,
          page: 1,
          sortBy: "created_at",
          sort: "DESC",
        });
        const existing = res?.totalCount || 0;
        const projectRef =
          item?.displayTitle || item?.project_number || item?.name || "";
        const title = t("project.checklist.default_title", {
          number: existing + 1,
          suffix: projectRef ? ` - ${projectRef}` : "",
        });
        if (!cancelled) {
          setChecklistDefaultTitle(title);
        }
      } catch (err) {
        console.error("Could not resolve default checklist title:", err);
        if (!cancelled) {
          setChecklistDefaultTitle(
            t("project.checklist.generic_title", {
              number: 1,
              date: new Date().toISOString().split("T")[0],
            })
          );
        }
      }
    };

    loadDefaultTitle();

    return () => {
      cancelled = true;
    };
  }, [checklistTypeSelectorOpen, item?.id, item?.displayTitle, item?.project_number, item?.name, t]);

  const handleChecklistSelect = useCallback(
    async (config) => {
      const title = String(config?.title || checklistDefaultTitle || "").trim();
      if (!title) {
        toast.error(t("project.checklist.title_required"));
        return;
      }

      setIsCreatingChecklist(true);
      try {
        const payload = {
          title,
          description: "",
          date: new Date().toISOString().split("T")[0],
          status: "open",
          project_id: item.id,
        };

        const customerId = item?.customer_id || item?.customer?.id;
        if (customerId) {
          payload.customer_id = customerId;
        }

        if (config?.mode === "type") {
          payload.checklist_type_id = config.checklist_type_id;
          if (config.selected_item_ids?.length) {
            payload.selected_item_ids = config.selected_item_ids;
          }
        } else if (config?.mode === "main_report" && config.items?.length) {
          payload.copy_from_checklist_items = config.items;
        }

        const response = await createChecklist(payload);

        if (response?.response?.status >= 400) {
          const apiErrors = response.response?.data?.errors;
          throw new Error(
            Array.isArray(apiErrors) ? apiErrors.join(", ") : t("project.checklist.create_failed")
          );
        }

        const checklistId = response?.id || response?.data?.id;

        if (checklistId) {
          toast.success(t("project.checklist.create_success"));
          setChecklistTypeSelectorOpen(false);
          navigate(`/dashboard/hse-ks/checklists/${checklistId}`);
        } else {
          const apiErrors = response?.response?.data?.errors || response?.errors;
          throw new Error(
            Array.isArray(apiErrors) ? apiErrors.join(", ") : t("project.checklist.id_missing")
          );
        }
      } catch (err) {
        console.error("Error creating checklist:", err);
        toast.error(t("project.checklist.create_failed"));
      } finally {
        setIsCreatingChecklist(false);
      }
    },
    [checklistDefaultTitle, item, navigate, t]
  );

  const [labelPrintDialogOpen, setLabelPrintDialogOpen] = useState(false);

  /** Archive tab opens the dedicated archive page with this project selected. */
  const openProjectArchive = useCallback(() => {
    if (!item?.id) return;
    navigate(projectArchivePath(item.id, searchParams.get("folder")));
  }, [item, navigate, searchParams]);

  /** Tegninger tab opens the drawings workspace with this project preselected. */
  const openProjectDrawings = useCallback(() => {
    if (!item?.id) return;
    const displayTitle =
      item.displayTitle ||
      [item.project_number, item.name].filter(Boolean).join(" ") ||
      item.name ||
      "";
    writeDrawingListState({
      searchQuery: "",
      projectId: item.id,
      projectTitle: displayTitle,
      selectedFloorId: null,
      selectedHouseId: null,
      selectedAndre: false,
    });
    navigate(`/dashboard/drawings?project_id=${item.id}`);
  }, [item, navigate]);

  const tabs = [
    {
      name: "Detaljer",
      component: <ProjectInfo item={item} onRefresh={handleOnRefresh} />,
      icon: PencilIcon,
      active: projectPermissions.canRead,
    },

    {
      name: t("project.tabs.updates"),
      icon: ChatBubbleLeftRightIcon,
      active: projectPermissions.canAddNotes,
      component: (
        <StatusUpdateSection
          subjectType="project"
          subjectId={item?.id}
          customer={item?.customer}
          project={item}
          canCompose={projectPermissions.canAddNotes}
        />
      ),
    },

    {
      name: t("project.hse_handbook.tab_title"),
      icon: BookOpenIcon,
      active: projectPermissions.canRead,
      component: <ProjectHseHandbookTab project={item} />,
    },

    {
      name: t("project.activities.tab_title"),
      component: (
        <ProjectActivitiesTab
          project={item}
          timeRegistrationTabIndex={14}
          workOrdersTabIndex={5}
        />
      ),
      icon: ListBulletIcon,
      active: projectPermissions.canRead,
    },

    {
      name: "Fields (DEV)",
      component: <FieldsSection module={"project"} moduleParam={item?.id} />,
      icon: TableCellsIcon,
      // Active false if production
      active: process.env.NODE_ENV !== "production",
    },

    {
      name: "Arbeidsordre",
      icon: WrenchScrewdriverIcon,
      active:
        workOrderAccess.canRead && projectPermissions.canCreateWorkOrders,
      component: (
        <WorkOrderStacklist
          module_param={item.id}
          module="project"
          fields="id, displayTitle, number, status, order_date, due_date, customer, project, responsible_participant, responsible_executor_participant, events, description, created_at"
          per_page="10"
          filters={[
            {
              name: "project",
              value: item.id,
            },
          ]}
        />
      ),
    },

    {
      name: t("module_overview.tab.reports"),
      icon: WrenchScrewdriverIcon,
      active:
        serviceReportAccess.canRead && projectPermissions.canCreateReports,
      component: (
        <ReportsStackList
          module_param={item.id}
          module="project"
          project={item}
          fields="id, displayTitle, report_number, status, executing_employee, delivery_address, customer, report_type, appointment, description"
          per_page="10"
          filters={[
            {
              name: "project",
              value: item.id,
            },
          ]}
        />
      ),
    },
    {
      name: t("document_archive.tab"),
      aliases: ["Folders"],
      icon: FolderIcon,
      active: projectPermissions.canReadFolders,
      // Keep the tab in the project UI; click opens the dedicated archive page.
      onSelect: openProjectArchive,
      component: null,
    },
    {
      name: "Tegninger",
      icon: DocumentTextIcon,
      active:
        drawingsModuleActive &&
        projectPermissions.canRead &&
        !projectPermissions.isExecutorParticipant,
      // Keep the tab in the project UI; click navigates to drawings with project selected.
      onSelect: openProjectDrawings,
      component: null,
    },
    {
      name: IFC_VIEWER_TAB_NAME,
      icon: CubeIcon,
      active:
        ifcViewerModuleActive &&
        ifcViewerAccess &&
        projectPermissions.canRead &&
        !projectPermissions.isExecutorParticipant,
      component: (
        <IfcViewerPanel
          project={item}
          canEdit={ifcViewerCanEdit}
          isAdmin={ifcViewerIsAdmin}
        />
      ),
    },
    {
      name: t("module_overview.tab.agreements"),
      icon: PaperClipIcon,
      active:
        serviceAgreementAccess.canRead &&
        projectPermissions.canCreateServiceAgreements,
      component: (
        <ServiceAgreementsStackList
          module_param={item.id}
          module="project"
          filters={[
            {
              name: "project",
              value: item.id,
            },
          ]}
        />
      ),
    },
    {
      name: "Sjekklister",
      icon: CheckCircleIcon,
      active:
        qualityChecklistAccess.canRead && projectPermissions.canEdit,
      component: (
        <ChecklistStacklist
          module_param={item.id}
          module="project"
          filters={[
            {
              name: "project",
              value: item.id,
            },
          ]}
        />
      ),
    },
    {
      name: t("project.tasks.tab_title"),
      icon: ClipboardDocumentCheckIcon,
      active: projectPermissions.canRead,
      component: <ProjectTasksTab project={item} canManageTasks={projectPermissions.canEdit} />,
    },

    {
      name: "Avvik",
      icon: PaperClipIcon,
      active:
        qualityDeviationAccess.canRead && projectPermissions.canEdit,
      component: (
        <DeviationStacklist
          module_param={item.id}
          module="project"
          filters={[
            {
              name: "project",
              value: item.id,
            },
          ]}
        />
      ),
    },

    {
      name: t("project.time_overview.tab"),
      tabKey: TIME_OVERVIEW_TAB_KEY,
      aliases: TIME_OVERVIEW_TAB_ALIASES,
      icon: ClockIcon,
      active: projectPermissions.canTimeRegister,
      component: (
        <TimeRegistrationTab
          project={item}
          canManageProjectTime={
            projectPermissions.canEdit || projectPermissions.isAdmin
          }
          showHourlyCosts={projectPermissions.canSeeProjectHourlyCosts}
          restrictHoursToCurrentUserId={
            projectPermissions.isExecutorParticipant
              ? currentUserId ?? null
              : null
          }
        />
      ),
    },

    {
      name: "Vareliste",
      tabKey: "item-lists",
      icon: ClipboardDocumentListIcon,
      active: projectPermissions.canReadItemLists,
      component: <ItemListTab project={item} />,
    },

    {
      name: "Fakturaer",
      icon: DocumentTextIcon,
      active: canProjectAccounting && projectPermissions.canRead,
      component: (
        <InvoicesTab key={item.id} project={item} canEdit={canEditAccountingUi} />
      ),
    },

    {
      name: "Leverandørfakturaer",
      icon: TruckIcon,
      active: canProjectAccounting && projectPermissions.canRead,
      component: (
        <SupplierInvoicesTab project={item} canEdit={canEditAccountingUi} />
      ),
    },

    {
      name: "Faktureringsplan",
      icon: ChartBarIcon,
      active: canProjectAccounting && projectPermissions.canRead,
      component: (
        <BillingPlanTab project={item} canEdit={canEditAccountingUi} />
      ),
    },

    {
      name: "Innstillinger",
      icon: CogIcon,
      active: projectPermissions.isAdmin,
      component: <ProjectSettingsTab project={item} onRefresh={handleOnRefresh} />,
    },

    {
      name: "Summering",
      icon: ChartBarIcon,
      active: canProjectAccounting && projectPermissions.canRead,
      component: <ProjectSummaryTab project={item} />,
    },

    {
      name: "Website",
      icon: GlobeAltIcon,
      active:
        websiteModuleActive &&
        projectPermissions.canRead &&
        !projectPermissions.isExecutorParticipant,
      component: <WebsiteTab project={item} onRefresh={handleOnRefresh} />,
    },

    {
      name: t("journal.journals"),
      icon: BookOpenIcon,
      active: projectPermissions.canRead,
      component: <JournalSection subjectType="Project" subjectId={item?.id} />,
    },

    {
      name: t("textDocuments.title"),
      icon: DocumentTextIcon,
      active: projectPermissions.canRead,
      component: (
        <TextDocumentsSection subjectType="Project" subjectId={item?.id} />
      ),
    },
  ];

  // Read directly from the URL so refresh and back/forward cannot race local state.
  const showMobileNav = Boolean(project?.id) && !isLoading && !error;
  const ifcParam = searchParams.get("ifc");
  const [selectedTab, handleSelectTab] = useUrlTab(tabs, {
    overrideTab: ifcParam === "1" || ifcParam === "true" ? IFC_VIEWER_TAB_NAME : undefined,
    clearParams: ["ifc"],
    mergeSearchParams: mergeRouterAndWindowSearchParams,
  });

  tabsRef.current = tabs;
  selectTabRef.current = handleSelectTab;

  const handleRunAction = useCallback(
    (action) => {
      if (!action) return;
      if (action.directUpload) {
        photoPickerRef.current?.();
        return;
      }
      if (action.action) {
        action.action();
        return;
      }
      if (action.path) navigate(action.path);
    },
    [navigate]
  );

  const handleExportOverviewPdf = useCallback(async () => {
    if (!project?.id || exportingPdf) return;
    setExportingPdf(true);
    try {
      await exportProjectOverviewPdf(project.id);
      toast.success(t("project.overview.export_pdf_success"));
    } catch (error) {
      const message =
        error.response?.data?.errors?.[0] ||
        error.message ||
        t("project.overview.export_pdf_error");
      toast.error(message);
    } finally {
      setExportingPdf(false);
    }
  }, [exportingPdf, project?.id, t]);

  // Archive and drawings retain their dedicated-page deep links, after access is known.
  const selectedTabAction = tabs[selectedTab]?.onSelect;
  useEffect(() => {
    if (!isLoading && !error && project?.id && selectedTabAction) selectedTabAction();
  }, [isLoading, error, project?.id, selectedTabAction]);

  const renderContent = () => {
    if (isLoading) {
      return <ResourceLoading />;
    }
    if (error && errors) {
      return <ResourceError errors={errors} />;
    }

    const overflowItems = [
      {
        label: t("project.overview.export_pdf"),
        action: handleExportOverviewPdf,
      },
      {
        label: t("project.actions.print_label"),
        icon: PrinterIcon,
        action: () => setLabelPrintDialogOpen(true),
      },
    ];

    const projectHero = (
      <ProjectViewHero
        project={project}
        onRefresh={handleOnRefresh}
        tabs={tabs}
        selectedTab={selectedTab}
        onSelectTab={handleSelectTab}
        hideMobileSelect={showMobileNav}
        onOpenPalette={() => setPaletteOpen(true)}
        onEdit={() => navigate(`/dashboard/projects/${project?.id}/edit`)}
        onExportPdf={handleExportOverviewPdf}
        exportingPdf={exportingPdf}
        canEdit={projectPermissions.canEdit}
        overflowItems={overflowItems}
        onRetrySync={handleRetryProjectSync}
        onSwitchProject={(next) => {
          if (!next?.id || String(next.id) === String(project?.id)) return;
          const tab = searchParams.get("tab");
          navigate(
            `/dashboard/projects/${next.id}${tab ? `?tab=${encodeURIComponent(tab)}` : ""}`,
          );
        }}
      />
    );

    const isArchiveTab =
      tabs[selectedTab]?.name === t("document_archive.tab");
    const isIfcTab = tabs[selectedTab]?.name === IFC_VIEWER_TAB_NAME;

    return (
      <div className="pv-scope">
        <DashboardHeader
          variant="v2"
          sticky
          subtitle=""
          title={project?.displayTitle}
          customHeader={projectHero}
          contentClassName={
            isArchiveTab
              ? "!mb-3 !ml-0 sm:!ml-0 min-w-0 overflow-x-clip"
              : isIfcTab
                ? "!mb-0 !ml-0 sm:!ml-0 min-w-0 overflow-hidden"
                : "!ml-0 sm:!ml-0 min-w-0 overflow-x-clip"
          }
        >
          <div
            className={
              isArchiveTab
                ? `mx-auto w-full min-w-0 max-w-[1600px] overflow-x-clip px-3 sm:px-6 ${
                    showMobileNav
                      ? "mt-0 pb-[calc(6.5rem+env(safe-area-inset-bottom,0px))] sm:mt-3 lg:pb-0"
                      : "mt-3"
                  }`
                : isIfcTab
                  ? `mx-auto w-full min-w-0 max-w-full overflow-hidden overflow-x-hidden px-0 sm:px-6 ${
                      showMobileNav
                        ? "mt-0 pb-0 lg:mt-6"
                        : "mt-0 lg:mt-6"
                    }`
                : `mx-auto w-full min-w-0 max-w-[1320px] overflow-x-clip px-3 sm:px-6 ${
                    showMobileNav
                      ? "mt-0 pb-[calc(6.5rem+env(safe-area-inset-bottom,0px))] sm:mt-6 lg:pb-0"
                      : "mt-6"
                  }`
            }
          >
            <div className={isArchiveTab || isIfcTab ? "min-h-0 w-full" : ""}>
              {tabs[selectedTab]?.component ?? null}
            </div>
          </div>

          {showMobileNav ? (
            <>
              {projectPermissions.canEdit && item?.id ? (
                <div className="sr-only" aria-hidden>
                  <DirectImageUploadButton
                    module="project"
                    moduleParam={item.id}
                    openPickerRef={photoPickerRef}
                    previewOptions={{
                      folderSupport: true,
                      folderProjectId: item.id,
                      drawingSupport: true,
                      project: item,
                    }}
                  />
                </div>
              ) : null}
              <ProjectMobileActionsNav
                actions={rightSideActions}
                tabs={tabs}
                selectedTab={selectedTab}
                setSelectedTab={handleSelectTab}
                project={item}
                onRefresh={handleOnRefresh}
                hideActionsSlot
                canManageContacts={
                  projectPermissions?.isAdmin ||
                  projectPermissions?.canEdit ||
                  projectPermissions?.canManageParticipants
                }
              />
              <ProjectFab
                actions={rightSideActions}
                onRunAction={handleRunAction}
                openPickerRef={photoPickerRef}
              />
            </>
          ) : null}
        </DashboardHeader>

        <ProjectCommandPalette
          open={paletteOpen}
          onOpen={() => setPaletteOpen(true)}
          onClose={() => setPaletteOpen(false)}
          projectId={item?.id}
          actions={rightSideActions}
          onRunAction={handleRunAction}
        />

        <ServiceReportEditModal
          customer={item?.customer}
          project={item}
          contact={item?.contact}
          delivery_address={item?.delivery_address}
          // fields={caseItemFields}
          open={reportDialogOpen}
          onClose={() => setReportDialogOpen(false)}
          selected={null}
          onRefresh={() => null}
        />

        <LabelDesignerDialog
          module="project"
          open={labelPrintDialogOpen && !!project?.id}
          onClose={() => setLabelPrintDialogOpen(false)}
          loadRecords={() => [projectRecord(project)]}
        />

        <FolderCreateDialog
          open={folderCreateDialogOpen}
          onClose={() => setFolderCreateDialogOpen(false)}
          project={item}
          isArchiveAdmin={projectPermissions.isAdmin}
          onFolderCreated={handleFolderCreated}
        />

        <EnhancedChecklistSelector
          open={checklistTypeSelectorOpen}
          onClose={() => {
            if (!isCreatingChecklist) {
              setChecklistTypeSelectorOpen(false);
            }
          }}
          onSelect={handleChecklistSelect}
          defaultTitle={checklistDefaultTitle}
          showTitleField
          autoTitleFromType
        />

      </div>
    );
  };

  return <>{renderContent()}</>;
};
